import { memo } from 'react';
import { Mic, Pause, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConnectionStatus } from '@/hooks/useDeepgramTranscriber';

interface ImmersiveRecordButtonProps {
  connectionStatus: ConnectionStatus;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
}

const ImmersiveRecordButton = memo<ImmersiveRecordButtonProps>(
  ({ connectionStatus, onStart, onPause, onResume }) => {
    const handleClick = () => {
      switch (connectionStatus) {
        case 'idle':
          onStart();
          break;
        case 'recording':
          onPause();
          break;
        case 'paused':
          onResume();
          break;
        // connecting / transcribing → disabled, no action
      }
    };

    const isDisabled = connectionStatus === 'connecting' || connectionStatus === 'transcribing';

    const baseClasses =
      'relative flex items-center justify-center rounded-2xl transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed w-16 h-16 sm:w-20 sm:h-20';

    const stateStyles: Record<ConnectionStatus, string> = {
      idle: 'bg-primary hover:bg-primary/90',
      connecting: 'bg-primary animate-pulse',
      recording: 'bg-primary hover:bg-primary/90',
      paused: 'bg-primary/80 hover:bg-primary/90',
      transcribing: 'bg-primary/30',
    };

    const renderIcon = () => {
      switch (connectionStatus) {
        case 'idle':
          return <Mic className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />;
        case 'connecting':
          return (
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-primary-foreground rounded-full animate-connecting-bounce" />
              <span className="w-2 h-2 bg-primary-foreground rounded-full animate-connecting-bounce animation-delay-200" />
              <span className="w-2 h-2 bg-primary-foreground rounded-full animate-connecting-bounce animation-delay-400" />
            </div>
          );
        case 'recording':
          return <Pause className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />;
        case 'paused':
          return <Mic className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />;
        case 'transcribing':
          return <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground animate-spin" />;
      }
    };

    return (
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className={cn(baseClasses, stateStyles[connectionStatus])}
        aria-label={
          connectionStatus === 'idle'
            ? '録音開始'
            : connectionStatus === 'recording'
            ? '一時停止'
            : connectionStatus === 'paused'
            ? '録音再開'
            : connectionStatus === 'connecting'
            ? '接続中'
            : '処理中'
        }
      >
        {/* Glow rings for recording state */}
        {connectionStatus === 'recording' && (
          <>
            <span className="absolute inset-0 rounded-2xl bg-primary/30 animate-glow-pulse" />
            <span className="absolute inset-0 rounded-2xl bg-primary/30 animate-glow-pulse animation-delay-500" />
            <span className="absolute inset-0 rounded-2xl bg-primary/30 animate-glow-pulse animation-delay-1000" />
          </>
        )}

        {/* Slow pulse for paused state */}
        {connectionStatus === 'paused' && (
          <span className="absolute inset-0 rounded-2xl bg-primary/20 animate-pulse" />
        )}

        {/* Icon layer (above glow rings) */}
        <span className="relative z-10">{renderIcon()}</span>
      </button>
    );
  }
);
ImmersiveRecordButton.displayName = 'ImmersiveRecordButton';

export default ImmersiveRecordButton;
